import React, { useEffect, useState } from "react";
import {
  BadgeCheck,
  BellRing,
  CreditCard,
  Filter,
  Flag,
  Inbox,
  Quote,
  LineChart,
  RefreshCw,
  Users,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Spinner } from "@/components/common/Loading";
import TrafficPanel from "@/components/admin/TrafficPanel";
import FunnelPanel from "@/components/admin/FunnelPanel";
import ContactInbox from "@/components/admin/ContactInbox";
import AdminOverview from "@/components/admin/AdminOverview";
import AdminUserManagement from "@/components/admin/AdminUserManagement";
import AdminSponsorProfiles from "@/components/admin/AdminSponsorProfiles";
import AdminReports from "@/components/admin/AdminReports";
import AdminSubscriptions from "@/components/admin/AdminSubscriptions";
import AdminBillingSync from "@/components/admin/AdminBillingSync";
import AdminTestimonials from "@/components/admin/AdminTestimonials";
import AdminAvailabilityAlerts from "@/components/admin/AdminAvailabilityAlerts";

import { toast } from "sonner";

const TABS = [
  { key: "overview", label: "Overview", icon: BadgeCheck },
  { key: "traffic", label: "Website Traffic", icon: LineChart },
  { key: "funnel", label: "Signup Funnel", icon: Filter },
  { key: "users", label: "Users", icon: Users },
  { key: "profiles", label: "Sponsor Profiles", icon: BadgeCheck },
  { key: "reports", label: "Reports", icon: Flag },
  { key: "contact", label: "Contact Inbox", icon: Inbox },
  { key: "testimonials", label: "Testimonials", icon: Quote },
  { key: "alerts", label: "Availability Alerts", icon: BellRing },
  { key: "subscriptions", label: "Subscriptions", icon: CreditCard },
  { key: "sync", label: "Billing Sync", icon: RefreshCw },
];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [testAccountCount, setTestAccountCount] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [reports, setReports] = useState([]);
  const [subs, setSubs] = useState({ subscriptions: [], payments: [], checkout_attempts: [] });
  const [sync, setSync] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [s, u, p, r, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/sponsor-profiles"),
        api.get("/admin/reports"),
        api.get("/admin/subscriptions"),
      ]);
      setStats(s.data);
      setUsers(u.data.users);
      setTestAccountCount(u.data.test_account_count ?? 0);
      setProfiles(p.data.sponsor_profiles);
      setReports(r.data.reports);
      setSubs(b.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadSync = async () => {
    try {
      const { data } = await api.get("/admin/billing-sync");
      setSync(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (tab === "sync") loadSync();
  }, [tab]);

  const searchUsers = async () => {
    const { data } = await api.get("/admin/users", { params: { q: q || undefined } });
    setUsers(data.users);
    setTestAccountCount(data.test_account_count ?? 0);
  };

  const purgeTestAccounts = async () => {
    setBusy("purge-test");
    try {
      const { data } = await api.post("/admin/users/purge-test-accounts");
      toast.success(
        data.removed.users
          ? `Removed ${data.removed.users} test account${data.removed.users === 1 ? "" : "s"}.`
          : "No test accounts left to remove."
      );
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const act = async (userId, action) => {
    setBusy(userId + action);
    try {
      await api.post(`/admin/users/${userId}/action`, { action });
      toast.success(`Member ${action === "activate" ? "reinstated" : action + "ed"}.`);
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const resolve = async (reportId, resolution, actionTaken) => {
    setBusy(reportId + resolution);
    try {
      await api.post(`/admin/reports/${reportId}/resolve`, {
        resolution,
        action_taken: actionTaken || null,
      });
      toast.success("Report updated.");
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (loading) return <Spinner label="Loading admin dashboard" />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="font-display text-3xl font-bold text-[#1F2A2E]">Admin Dashboard</h1>
      <p className="mt-1.5 text-[#4B5A60]">Moderation, membership and community health.</p>

      <div className="mt-5 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border-2 bg-white ${
              tab === t.key
                ? "border-[#1F6F78] text-[#1F6F78] bg-[#F2FAFA]"
                : "border-[#E3DED4] text-[#4B5A60]"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
            {t.key === "reports" && stats?.reports.open > 0 && (
              <span className="ml-1 px-1.5 rounded-full bg-[#B42318] text-white text-[10px]">
                {stats.reports.open}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "overview" && <AdminOverview stats={stats} />}

      {tab === "traffic" && <TrafficPanel />}

      {tab === "funnel" && <FunnelPanel />}

      {tab === "contact" && <ContactInbox />}

      {tab === "testimonials" && <AdminTestimonials />}

      {tab === "alerts" && <AdminAvailabilityAlerts />}

      {tab === "users" && <AdminUserManagement users={users} testAccountCount={testAccountCount} q={q} setQ={setQ} busy={busy} searchUsers={searchUsers} purgeTestAccounts={purgeTestAccounts} act={act} />}

      {tab === "profiles" && <AdminSponsorProfiles profiles={profiles} busy={busy} setBusy={setBusy} loadAll={loadAll} />}

      {tab === "reports" && <AdminReports reports={reports} act={act} resolve={resolve} busy={busy} />}

      {tab === "subscriptions" && <AdminSubscriptions subs={subs} />}

      {tab === "sync" && <AdminBillingSync sync={sync} busy={busy} setBusy={setBusy} loadAll={loadAll} loadSync={loadSync} />}
    </div>
  );
}
